import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import type { NotificationFeedItem } from "@situate/contracts/runtime";
import { NotificationListItem } from "@/features/deployment-dashboard/widgets/panels/notification-list/notification-list-item";
import { ConsoleSurface } from "@/shared/ui";

interface NotificationListProps {
  notifications: readonly NotificationFeedItem[];
  selectedNotificationId?: string | null;
  onSelect?: (notification: NotificationFeedItem) => void;
}

interface NotificationListEntry {
  notification: NotificationFeedItem;
  notifications: NotificationFeedItem[];
  notificationIds: Set<string>;
}

function isNewerNotification(
  candidate: NotificationFeedItem,
  current: NotificationFeedItem
): boolean {
  const candidateTime = new Date(candidate.created_at).getTime();
  const currentTime = new Date(current.created_at).getTime();
  return (
    candidateTime > currentTime ||
    (candidateTime === currentTime &&
      candidate.notification_id.localeCompare(current.notification_id) > 0)
  );
}

function groupNotifications(
  notifications: readonly NotificationFeedItem[]
): NotificationListEntry[] {
  const entries: NotificationListEntry[] = [];
  const alertEntries = new Map<string, NotificationListEntry>();

  for (const notification of notifications) {
    if (notification.type !== "alert") {
      entries.push({
        notification,
        notifications: [notification],
        notificationIds: new Set([notification.notification_id]),
      });
      continue;
    }

    const existingEntry = alertEntries.get(notification.source.id);
    if (existingEntry !== undefined) {
      if (!existingEntry.notificationIds.has(notification.notification_id)) {
        existingEntry.notificationIds.add(notification.notification_id);
        existingEntry.notifications.push(notification);
      }
      if (isNewerNotification(notification, existingEntry.notification)) {
        existingEntry.notification = notification;
      }
      continue;
    }

    const entry = {
      notification,
      notifications: [notification],
      notificationIds: new Set([notification.notification_id]),
    };
    alertEntries.set(notification.source.id, entry);
    entries.push(entry);
  }

  return entries;
}

/**
 * Vertical list of notification rows. Renders an empty-state message when no
 * notifications match the current filters instead of an empty list. The
 * The caller supplies the validated Runtime API notification feed.
 */
export function NotificationList({
  notifications,
  selectedNotificationId = null,
  onSelect,
}: NotificationListProps) {
  const [expandedAlertIds, setExpandedAlertIds] = useState<ReadonlySet<string>>(() => new Set());

  if (notifications.length === 0) {
    return (
      <ConsoleSurface sx={{ px: 2.5, py: 4, textAlign: "center" }}>
        <Typography variant="subtitle2">No notifications match the selected filters.</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Adjust or reset the filters above to see more results.
        </Typography>
      </ConsoleSurface>
    );
  }

  const groupedNotifications = groupNotifications(notifications);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {groupedNotifications.map(({ notification, notifications: groupedItems }) => {
        const alertId = notification.type === "alert" ? notification.source.id : null;
        const isExpanded = alertId !== null && expandedAlertIds.has(alertId);
        const canExpand = groupedItems.length > 1 && alertId !== null;

        return (
          <Box key={notification.notification_id}>
            {!canExpand && (
              <NotificationListItem
                notification={notification}
                notificationCount={groupedItems.length}
                selected={notification.source.id === selectedNotificationId}
                onSelect={onSelect}
              />
            )}
            {canExpand && (
              <>
                <Box
                  sx={{
                    position: "relative",
                    pb: isExpanded ? 0 : 1.25,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 4,
                      borderRadius: 1,
                      backgroundColor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      transform: "translateY(4px)",
                      zIndex: 0,
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: 2,
                      borderRadius: 1,
                      backgroundColor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      transform: "translateY(2px)",
                      zIndex: 0,
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <NotificationListItem
                      notification={notification}
                      notificationCount={groupedItems.length}
                      selected={notification.source.id === selectedNotificationId}
                      onSelect={onSelect}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (alertId === null) return;
                      setExpandedAlertIds((current) => {
                        const next = new Set(current);
                        if (next.has(alertId)) next.delete(alertId);
                        else next.add(alertId);
                        return next;
                      });
                    }}
                    aria-label={
                      isExpanded ? "Hide grouped notifications" : "Show grouped notifications"
                    }
                    aria-expanded={isExpanded}
                    sx={(theme) => ({
                      position: "absolute",
                      right: 12,
                      bottom: isExpanded ? -8 : -2,
                      zIndex: 2,
                      p: 0.25,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: theme.shadows[2],
                      "&:hover": { backgroundColor: theme.palette.action.hover },
                    })}
                  >
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={isExpanded} unmountOnExit>
                  <Box
                    sx={(theme) => ({
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      mt: 1,
                      ml: 1.5,
                      pl: 1.5,
                      borderLeft: `2px solid ${theme.palette.divider}`,
                    })}
                  >
                    {groupedItems
                      .filter(
                        (groupedNotification) =>
                          groupedNotification.notification_id !== notification.notification_id
                      )
                      .map((groupedNotification) => (
                        <NotificationListItem
                          key={groupedNotification.notification_id}
                          notification={groupedNotification}
                          onSelect={onSelect}
                        />
                      ))}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
